// Round «errori onesti» (2026-08-03) — the error must accuse the right party.
//
// api-js validates every response against the contract it was built from. When
// the backend serves a value this client does not know — a driver, a phase, a
// code added by a later round — the HTTP call SUCCEEDED (200) and the value is
// legitimate: it is the web interface that is behind. Before this helper the
// failure arrived as a bare ZodError and every view collapsed it into "is the
// TVirt backend reachable?", sending the operator to look at a service that is
// answering perfectly (bug hunt #157, finding U1: one `nvme-tcp` storage killed
// the whole Home dashboard, and the retry loop killed it again every 15 s).
//
// So: a response the client cannot read says exactly that, and NAMES the field
// and the value. A network that is really down keeps the old message, which is
// true only there.

import { TVirtApiError, TVirtResponseError, TVirtTaskError } from 'trantor-tvirt-api-js'

import { clusterDebugMode } from '@/libs/clusterDebug'


type Translate = (key: string) => string


export const contractErrorText = (err: TVirtResponseError, t: Translate): string =>
  t('common.contractError').replace('{field}', err.field).replace('{value}', err.value)


// The one-line message a view shows when a load failed. `fallbackKey` is the
// view's own "could not load" wording — reached ONLY when the failure is not a
// contract violation and not a problem+json the backend explained itself.
export const describeLoadError = (err: unknown, t: Translate, fallbackKey: string): string => {
  if (err instanceof TVirtResponseError) {
    return contractErrorText(err, t)
  }

  if (err instanceof TVirtApiError) {
    return err.message
  }

  return t(fallbackKey)
}


// The one-line message a view shows when an ACTION failed (#260 part B —
// Luca's dictation 2026-08-09). The SINGLE point every generic action catch
// goes through: a problem+json the backend explained (TVirtApiError) or a
// failed task (TVirtTaskError) speaks its own message; anything else — the
// contract violation of #260, a network error, a client bug — collapses into
// the view's own `fallbackKey` wording, EXCEPT that with the cluster's
// debugMode ON the underlying cause is appended, so «could not complete»
// stops hiding what actually happened. OFF keeps today's behavior to the
// letter. Never re-implement this ternary in a catch: new call sites use
// this helper, and the debug plumbing reaches them for free.
export const describeActionError = (err: unknown, t: Translate, fallbackKey: string): string => {
  if (err instanceof TVirtApiError || err instanceof TVirtTaskError) {
    return err.message
  }

  const generic = t(fallbackKey)

  return clusterDebugMode() ? `${generic} [debug: ${String(err)}]` : generic
}


// R6 fase 2 (2026-08-24) — the optimistic-concurrency pair, ONE home for the
// whole GUI (the CLI lesson: translated per dialog, the thirteenth forgets it).
//
// `preconditionOf` is the only reader of the opaque `etag` a config resource
// serves (R6 fase 1): taken from the document the form was seeded from — a
// LIST row already carries it, no second read — never parsed, its only defined
// operation is equality. A document without one (a backend older than R6, a
// resource outside the perimeter) files nothing and the write behaves as today.
export const preconditionOf = (doc: Record<string, unknown> | { etag?: unknown } | undefined): { ifMatch?: string } => {
  const etag = doc !== undefined && 'etag' in doc ? doc.etag : undefined

  return typeof etag === 'string' ? { ifMatch: etag } : {}
}


// `isStaleWrite` is the ONE discriminant for the concurrency 412: the If-Match
// guard's detail always names the two validators («If-Match …, current …»),
// while the OTHER `precondition_failed` lanes (a VM's stopped-only field, a
// template, a busy task) never say «If-Match» and must keep their own words —
// telling an operator «someone else changed it» over a phase refusal would lie.
export const isStaleWrite = (err: unknown): boolean =>
  err instanceof TVirtApiError &&
  err.code === 'precondition_failed' &&
  err.detail !== undefined &&
  err.detail.includes('If-Match')


// The banner for a list that came back SHORT: api-js keeps the items it can
// read and hands the others over here, so one unreadable object no longer
// blanks the page — but the operator is told the list is incomplete, and why.
export const describeUnreadableItems = (unreadable: TVirtResponseError[], t: Translate): string | null => {
  const first = unreadable[0]

  if (first === undefined) {
    return null
  }

  return t('common.contractErrorItems')
    .replace('{count}', String(unreadable.length))
    .replace('{field}', first.field)
    .replace('{value}', first.value)
}
